import React, { useEffect } from 'react';
import { Edit, useForm } from '@refinedev/antd';
import { Form, Input, InputNumber, Select, DatePicker, message } from 'antd';
import {
  HealthSyncCategory,
  HealthSyncRecordAdminDto,
} from '@wellverse/shared-ts';
import dayjs from 'dayjs';
import { parseOptionalJsonField } from './json-field';

export const HealthSyncRecordEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult, form } =
    useForm<HealthSyncRecordAdminDto>({
      resource: 'health-sync-records',
    });

  const record = queryResult?.data?.data;

  useEffect(() => {
    if (!record || !form) return;
    form.setFieldsValue({
      category: record.category,
      dataType: record.dataType,
      numericValue: record.numericValue,
      unit: record.unit,
      sourceName: record.sourceName,
      startTime: record.startTime ? dayjs(record.startTime) : undefined,
      endTime: record.endTime ? dayjs(record.endTime) : undefined,
      payloadText: record.payload ? JSON.stringify(record.payload, null, 2) : '',
    });
  }, [record, form]);

  return (
    <Edit resource="health-sync-records" saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        layout="vertical"
        onFinish={(values: Record<string, unknown>) => {
          const parsed = parseOptionalJsonField(values.payloadText);
          if (!parsed.ok) {
            message.error(parsed.error);
            return;
          }

          const payload: Record<string, unknown> = {
            category: values.category,
            dataType: values.dataType,
            numericValue: values.numericValue,
            unit: values.unit,
            sourceName: values.sourceName,
            startTime: values.startTime
              ? dayjs(values.startTime as string).toISOString()
              : undefined,
            endTime: values.endTime
              ? dayjs(values.endTime as string).toISOString()
              : null,
            payload: parsed.value,
          };
          return formProps.onFinish?.(payload);
        }}
      >
        <Form.Item label="User">
          <Input
            disabled
            value={
              record?.userFullName
                ? `${record.userFullName} (${record.userEmail ?? record.userId})`
                : record?.userId
            }
          />
        </Form.Item>
        <Form.Item label="Platform">
          <Input disabled value={record?.platform} />
        </Form.Item>
        <Form.Item label="External Key">
          <Input disabled value={record?.externalKey} />
        </Form.Item>
        <Form.Item label="Category" name="category" rules={[{ required: true }]}>
          <Select
            options={Object.values(HealthSyncCategory).map((c) => ({
              label: c,
              value: c,
            }))}
          />
        </Form.Item>
        <Form.Item label="Data Type" name="dataType" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Numeric Value" name="numericValue">
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Unit" name="unit">
          <Input />
        </Form.Item>
        <Form.Item label="Source Name" name="sourceName">
          <Input />
        </Form.Item>
        <Form.Item
          label="Start Time"
          name="startTime"
          rules={[{ required: true, message: 'Start time is required' }]}
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="End Time" name="endTime">
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Payload (JSON)" name="payloadText">
          <Input.TextArea rows={8} />
        </Form.Item>
      </Form>
    </Edit>
  );
};
